import { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { format } from 'date-fns';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';

// ant design icons
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import PauseCircleOutlined from '@ant-design/icons/PauseCircleOutlined';
import StopOutlined from '@ant-design/icons/StopOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';

// project imports
import MainCard from 'components/MainCard';
import { CSVExport } from 'components/third-party/react-table';
import { getClientDataCollections } from 'api/dataCollection';
import { getEntriesForChild, FORM_TYPES } from 'utils/offlineFirstDataLayer';
import SpeechTextField from 'components/SpeechTextField';
import OtherBehaviorsSummary from 'components/OtherBehaviorsSummary';
import { ownIntervalRecordings } from 'utils/behaviorInterval';

// Custom soft red color
const SOFT_RED = '#e57373';

// Day-level equality used to split loaded sessions between the Data Entry
// tab (today's recordings) and the Historical Data tab (older recordings).
const isSameDayAsToday = (d) => {
  if (!d) return false;
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return false;
  const today = new Date();
  return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
};

// Historical sessions are loaded from the API; start with empty state

/**
 * OTHER BEHAVIOURS PER INTERVAL. Besides the target behaviour (whose W/P/N drives
 * every calculation), the therapist can note which OTHER behaviours occurred in an
 * interval. Stored on the session as `otherBehaviors` - one array of behaviour
 * values per interval, aligned with `data` - plus `otherBehaviorCounts`
 * ({ value: intervals it occurred in }) and `otherBehaviorLabels` ({ value: name })
 * so viewers and reports can read it without the behaviour list. Informational
 * only: it never changes the target behaviour's counts or percentages.
 */
const alignOtherBehaviors = (list, length, exclude) =>
  Array.from({ length }, (_, i) => {
    const cell = Array.isArray(list) && Array.isArray(list[i]) ? list[i] : [];
    return cell.filter((v) => v && v !== exclude);
  });

const summarizeOtherBehaviors = (list, labelOf) => {
  const counts = {};
  const labels = {};
  (list || []).forEach((cell) =>
    (cell || []).forEach((v) => {
      counts[v] = (counts[v] || 0) + 1;
      labels[v] = labelOf(v);
    })
  );
  return { otherBehaviorCounts: counts, otherBehaviorLabels: labels };
};

const BehaviorIntervalMode = ({
  onDataChange,
  viewMode = 'individual',
  childId,
  childName = '',
  therapistId,
  therapistName = '',
  sessionId,
  // Behaviors derived from the child's IEP / reference behaviors (same list
  // that powers the Behavior Tracking cards). When provided, the Target
  // Behavior dropdown is populated from this list; otherwise we fall back to
  // the hardcoded generic set so the form still works in demo / offline mode.
  behaviors,
  // Plain ref object the parent can use to invoke imperative actions
  // (currently: end + save the in-flight recording in one shot). We don't
  // use forwardRef + useImperativeHandle because the parent only needs to
  // call methods, not access DOM nodes.
  actionsRef
}) => {
  const theme = useTheme();
  const intl = useIntl();

  // Target behaviors list — prefer IEP-derived behaviors when available.
  const TARGET_BEHAVIORS = useMemo(() => {
    if (Array.isArray(behaviors) && behaviors.length > 0) {
      return behaviors.map((b) => ({ value: String(b.id), label: b.name }));
    }
    return [
      { value: 'on-task', label: intl.formatMessage({ id: 'form.behaviorInterval.onTask' }) },
      { value: 'off-task', label: intl.formatMessage({ id: 'form.behaviorInterval.offTask' }) },
      { value: 'self-stim', label: intl.formatMessage({ id: 'form.behaviorInterval.selfStim' }) },
      { value: 'verbal', label: intl.formatMessage({ id: 'form.behaviorInterval.verbal' }) },
      { value: 'aggression', label: intl.formatMessage({ id: 'form.behaviorInterval.aggression' }) },
      { value: 'compliance', label: intl.formatMessage({ id: 'form.behaviorInterval.compliance' }) },
      { value: 'attention', label: intl.formatMessage({ id: 'form.behaviorInterval.attention' }) },
      { value: 'social', label: intl.formatMessage({ id: 'form.behaviorInterval.social' }) },
      { value: 'other', label: intl.formatMessage({ id: 'form.behaviorInterval.other' }) }
    ];
  }, [behaviors, intl]);

  // Interval length options
  const INTERVAL_LENGTHS = [
    { value: 10, label: intl.formatMessage({ id: 'form.behaviorInterval.seconds10' }) },
    { value: 15, label: intl.formatMessage({ id: 'form.behaviorInterval.seconds15' }) },
    { value: 30, label: intl.formatMessage({ id: 'form.behaviorInterval.seconds30' }) },
    { value: 60, label: intl.formatMessage({ id: 'form.behaviorInterval.seconds60' }) }
  ];

  // Duration options
  const DURATION_OPTIONS = [
    { value: 5, label: intl.formatMessage({ id: 'form.behaviorInterval.minutes5' }) },
    { value: 10, label: intl.formatMessage({ id: 'form.behaviorInterval.minutes10' }) },
    { value: 15, label: intl.formatMessage({ id: 'form.behaviorInterval.minutes15' }) },
    { value: 20, label: intl.formatMessage({ id: 'form.behaviorInterval.minutes20' }) },
    { value: 30, label: intl.formatMessage({ id: 'form.behaviorInterval.minutes30' }) }
  ];

  // Interval type options
  const INTERVAL_TYPES = [
    { value: 'whole', label: intl.formatMessage({ id: 'form.behaviorInterval.wholeInterval' }) },
    { value: 'partial', label: intl.formatMessage({ id: 'form.behaviorInterval.partialInterval' }) },
    {
      value: 'momentary',
      label: intl.formatMessage({ id: 'form.behaviorInterval.momentary', defaultMessage: 'Momentary Time Sampling' })
    }
  ];

  // Session list state
  const [sessions, setSessions] = useState([]);

  // Saving state to prevent duplicate saves
  // eslint-disable-next-line no-unused-vars
  const [saving, setSaving] = useState(false);

  // Current session state
  const [showRecording, setShowRecording] = useState(false);
  const [editingSession, setEditingSession] = useState(null);

  // Recording settings
  const [selectedBehavior, setSelectedBehavior] = useState('');
  const [intervalType, setIntervalType] = useState('whole');
  const [intervalLength, setIntervalLength] = useState(30);
  const [customIntervalLength, setCustomIntervalLength] = useState('');
  const [useCustomInterval, setUseCustomInterval] = useState(false);
  const [overallDuration, setOverallDuration] = useState(10);
  const [customDuration, setCustomDuration] = useState('');
  const [useCustomDuration, setUseCustomDuration] = useState(false);
  // Momentary Time Sampling: the second within each interval at which the
  // observation is taken. Defaults to the end of the interval; user can drag
  // anywhere between 0 and the interval length (kept in sync via the effect
  // below).
  const [momentSecond, setMomentSecond] = useState(30);

  // Timer state
  const [timerRunning, setTimerRunning] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [currentIntervalIndex, setCurrentIntervalIndex] = useState(1);

  // Interval data: null, 'whole', 'part', 'none', 'missed'
  const [intervalData, setIntervalData] = useState([]);
  // Other behaviours seen in each interval (array of behaviour values per interval).
  const [otherBehaviors, setOtherBehaviors] = useState([]);
  const [otherMenu, setOtherMenu] = useState({ anchorEl: null, index: null });

  // Notes
  const [notes, setNotes] = useState('');

  // Missed interval toast state
  const [missedToast, setMissedToast] = useState({ open: false, intervalIndex: null });
  const missedToastTimerRef = useRef(null);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);

  // Grid view state
  const [gridTabValue, setGridTabValue] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);

  // Inline editing state for grid view (count-based entry)
  const [inlineEditing, setInlineEditing] = useState(false);
  const [inlineBehavior, setInlineBehavior] = useState('');
  const [inlineIntervalType, setInlineIntervalType] = useState('whole');
  const [inlineIntervalLength, setInlineIntervalLength] = useState(30);
  const [inlineOverallDuration, setInlineOverallDuration] = useState(10);
  const [inlineWholeCount, setInlineWholeCount] = useState(0);
  const [inlinePartialCount, setInlinePartialCount] = useState(0);
  const [inlineNoneCount, setInlineNoneCount] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [inlineNotes, setInlineNotes] = useState('');

  // Grid wrapper ref for scrolling
  const gridWrapperRef = useRef(null);
  const timerRef = useRef(null);

  // Auto-save: skip the initial render
  const initialRenderRef = useRef(true);

  // Calculate total intervals
  const totalIntervals = useMemo(() => {
    const actualInterval = useCustomInterval ? parseInt(customIntervalLength) || 30 : intervalLength;
    const actualDuration = useCustomDuration ? parseInt(customDuration) || 10 : overallDuration;
    const durationInSeconds = actualDuration * 60;
    return Math.max(1, Math.floor(durationInSeconds / actualInterval));
  }, [intervalLength, customIntervalLength, useCustomInterval, overallDuration, customDuration, useCustomDuration]);

  // Get actual interval length
  const actualIntervalLength = useMemo(() => {
    return useCustomInterval ? parseInt(customIntervalLength) || 30 : intervalLength;
  }, [intervalLength, customIntervalLength, useCustomInterval]);

  // Keep the momentary-sample second at the end of the interval by default
  // (and clamp it whenever the interval length shrinks).
  useEffect(() => {
    setMomentSecond((prev) => {
      if (prev > actualIntervalLength || prev <= 0) return actualIntervalLength;
      return prev;
    });
  }, [actualIntervalLength]);

  // Load historical Behavior Interval sessions for this child on mount so the
  // "Historical Data" tab shows past entries (including ones recorded in other
  // therapy sessions). Each backend row's raw_data either has a `sessions`
  // array (one row per therapy session, multiple recordings inside) or is a
  // single-session record — handle both shapes.
  useEffect(() => {
    if (!childId) return;
    let cancelled = false;
    (async () => {
      let backendItems = [];
      try {
        const res = await getClientDataCollections(childId, {
          datasheet_type_code: 'behavior_interval',
          page_size: 200
        });
        backendItems = res?.items || [];
      } catch (err) {
        console.warn('[BehaviorIntervalMode] Failed to load historical sessions from backend:', err?.message);
      }

      // Also pull pending (not-yet-synced) entries from IndexedDB. Without
      // this, a session committed via "End & Save" in the overflow modal is
      // invisible until the whole session is submitted and synced — auto-save
      // writes to IndexedDB with triggerSync:false on purpose, so the backend
      // doesn't see it yet.
      let pendingItems = [];
      try {
        pendingItems = (await getEntriesForChild(FORM_TYPES.BEHAVIOR_INTERVAL, childId)) || [];
      } catch (err) {
        console.warn('[BehaviorIntervalMode] Failed to read pending sessions from IndexedDB:', err?.message);
      }

      if (cancelled) return;

      const collected = [];
      for (const record of backendItems) {
        const raw = record?.raw_data || {};
        const inner = Array.isArray(raw.sessions) ? raw.sessions : null;
        if (inner && inner.length > 0) {
          for (const s of inner) {
            collected.push({
              ...s,
              id: s.id || `${record.id}:${s.date || Math.random()}`,
              date: s.date || record.collected_at,
              // The therapy session this recording was made in (none for a stale
              // copy of another session's recording embedded by older saves).
              _sessionId: s._historical ? s._sessionId : String(record.session_id || ''),
              // Mark as historical so the Data Entry tab can exclude these
              // and the Historical Data tab can include them.
              _historical: true
            });
          }
        } else if (raw.behavior || raw.intervalType) {
          // Flat single-session shape
          collected.push({
            _sessionId: String(record.session_id || ''),
            id: record.id,
            date: record.collected_at,
            behavior: raw.behavior,
            intervalType: raw.intervalType,
            intervalLength: raw.intervalLength,
            momentSecond: raw.momentSecond ?? null,
            totalIntervals: raw.totalIntervals,
            data: raw.data || [],
            notes: raw.sessionNotes || raw.notes || '',
            wholeCount: raw.wholeCount || 0,
            partialCount: raw.partialCount || 0,
            noneCount: raw.noneCount || 0,
            missedCount: raw.missedCount || 0,
            wholePercent: raw.wholePercent ?? raw.wholePct ?? 0,
            partialPercent: raw.partialPercent ?? raw.partialPct ?? 0,
            nonePercent: raw.nonePercent ?? raw.nonePct ?? 0,
            missedPercent: raw.missedPercent ?? raw.missedPct ?? 0,
            totalRecorded: raw.totalRecorded || 0,
            _historical: true
          });
        }
      }

      // Merge in not-yet-synced IndexedDB sessions. Each IDB entry's `data`
      // mirrors the formData shape that triggerAutoSave persisted, so its
      // `sessions[]` is the source of truth for in-flight entries.
      for (const entry of pendingItems) {
        if (entry?.syncStatus === 'synced' || entry?.synced) continue;
        const raw = entry?.data || {};
        const inner = Array.isArray(raw.sessions) ? raw.sessions : null;
        if (inner && inner.length > 0) {
          for (const s of inner) {
            collected.push({
              ...s,
              id: s.id || `idb-${entry.id}:${s.date || Math.random()}`,
              date: s.date || entry.sessionDate || entry.createdAt,
              _sessionId: s._historical ? s._sessionId : String(entry.sessionId || ''),
              _historical: true
            });
          }
        } else if (raw.behavior || raw.intervalType) {
          collected.push({
            _sessionId: String(entry.sessionId || ''),
            id: `idb-${entry.id}`,
            date: entry.sessionDate || entry.createdAt,
            behavior: raw.behavior,
            intervalType: raw.intervalType,
            intervalLength: raw.intervalLength,
            momentSecond: raw.momentSecond ?? null,
            totalIntervals: raw.totalIntervals,
            data: raw.data || [],
            notes: raw.sessionNotes || raw.notes || '',
            wholeCount: raw.wholeCount || 0,
            partialCount: raw.partialCount || 0,
            noneCount: raw.noneCount || 0,
            missedCount: raw.missedCount || 0,
            wholePercent: raw.wholePercent ?? raw.wholePct ?? 0,
            partialPercent: raw.partialPercent ?? raw.partialPct ?? 0,
            nonePercent: raw.nonePercent ?? raw.nonePct ?? 0,
            missedPercent: raw.missedPercent ?? raw.missedPct ?? 0,
            totalRecorded: raw.totalRecorded || 0,
            _historical: true
          });
        }
      }

      // Dedupe by id (multiple backend rows can carry overlapping sessions
      // arrays as they're upserted across saves).
      const seen = new Set();
      const deduped = collected.filter((s) => {
        if (!s.id) return true;
        const key = String(s.id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      // Newest first.
      deduped.sort((a, b) => {
        const ta = a?.date ? new Date(a.date).getTime() : 0;
        const tb = b?.date ? new Date(b.date).getTime() : 0;
        return tb - ta;
      });

      setSessions((prev) => {
        // Keep any sessions added during the load (rare, but possible if
        // the user starts recording immediately) at the top.
        const existingIds = new Set(prev.map((s) => String(s.id)));
        const merged = [...prev, ...deduped.filter((s) => !existingIds.has(String(s.id)))];
        return merged;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [childId]);

  // Calculate inline interval count automatically
  const inlineIntervalCount = useMemo(() => {
    const durationInSeconds = inlineOverallDuration * 60;
    return Math.max(1, Math.floor(durationInSeconds / inlineIntervalLength));
  }, [inlineOverallDuration, inlineIntervalLength]);

  // Timer effect
  useEffect(() => {
    if (timerRunning) {
      timerRef.current = setInterval(() => {
        setSeconds((prev) => {
          const newSeconds = prev + 1;
          const secInInterval = newSeconds % actualIntervalLength; // 0 at end of interval

          // Momentary Time Sampling: fire the Y/N toast at the sampled second
          // within the current interval (rather than only at the end). The
          // end-of-interval branch below still advances + marks 'missed' if the
          // cell is still null. When momentSecond === actualIntervalLength
          // (default), the sample point coincides with end-of-interval, so the
          // existing end-of-interval branch already handles it.
          if (intervalType === 'momentary' && momentSecond > 0 && momentSecond < actualIntervalLength && secInInterval === momentSecond) {
            const sampleIdx = currentIntervalIndex - 1;
            setIntervalData((prevData) => {
              if (prevData[sampleIdx] === null) {
                if (missedToastTimerRef.current) clearTimeout(missedToastTimerRef.current);
                setMissedToast({ open: true, intervalIndex: sampleIdx });
                missedToastTimerRef.current = setTimeout(() => {
                  setMissedToast((p) => (p.intervalIndex === sampleIdx ? { open: false, intervalIndex: null } : p));
                }, 5000);
              }
              return prevData;
            });
          }

          // End-of-interval: advance index and mark 'missed' if the cell is
          // still null (works for Whole / Partial / Momentary).
          if (secInInterval === 0) {
            const missedIdx = currentIntervalIndex - 1;

            setIntervalData((prevData) => {
              const newData = [...prevData];
              if (newData[missedIdx] === null) {
                newData[missedIdx] = 'missed';

                // Show missed toast with Y/N options
                if (missedToastTimerRef.current) clearTimeout(missedToastTimerRef.current);
                setMissedToast({ open: true, intervalIndex: missedIdx });
                missedToastTimerRef.current = setTimeout(() => {
                  setMissedToast((p) => (p.intervalIndex === missedIdx ? { open: false, intervalIndex: null } : p));
                }, 5000);
              }
              return newData;
            });

            setCurrentIntervalIndex((prevIndex) => {
              const nextIndex = prevIndex + 1;
              if (nextIndex > totalIntervals) {
                // End of session
                setTimerRunning(false);
                return totalIntervals;
              }
              return nextIndex;
            });
          }

          return newSeconds;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timerRunning, actualIntervalLength, currentIntervalIndex, totalIntervals, intervalType, momentSecond]);

  // Format timer display
  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60)
      .toString()
      .padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${mins}:${s}`;
  };

  // Calculate summaries
  const summary = useMemo(() => {
    const totalIntervalCount = intervalData.length;
    const recorded = intervalData.filter((v) => v !== null && v !== 'missed');
    const totalRecorded = recorded.length;

    // Count each type
    const wholeCount = intervalData.filter((v) => v === 'whole').length;
    const partialCount = intervalData.filter((v) => v === 'part').length;
    const noneCount = intervalData.filter((v) => v === 'none').length;
    const missedCount = intervalData.filter((v) => v === 'missed').length;

    // Calculate percentages based on total intervals
    const wholePct = totalIntervalCount > 0 ? Math.round((wholeCount / totalIntervalCount) * 100) : 0;
    const partialPct = totalIntervalCount > 0 ? Math.round((partialCount / totalIntervalCount) * 100) : 0;
    const nonePct = totalIntervalCount > 0 ? Math.round((noneCount / totalIntervalCount) * 100) : 0;
    const missedPct = totalIntervalCount > 0 ? Math.round((missedCount / totalIntervalCount) * 100) : 0;

    // Legacy calculations for backward compatibility
    const wholeYes = wholeCount;
    const wholeNo = totalRecorded - wholeYes;
    const partialYes = wholeCount + partialCount;
    const partialNo = totalRecorded - partialYes;

    return {
      wholeCount,
      partialCount,
      noneCount,
      missedCount,
      wholePct,
      partialPct,
      nonePct,
      missedPct,
      wholeYes,
      wholeNo,
      partialYes,
      partialNo,
      totalRecorded,
      totalIntervalCount
    };
  }, [intervalData]);

  // Start timer
  const handleStartTimer = () => {
    if (!sessionStarted) {
      // Initialize interval data
      setIntervalData(new Array(totalIntervals).fill(null));
      setOtherBehaviors(alignOtherBehaviors([], totalIntervals));
      setSeconds(0);
      setCurrentIntervalIndex(1);
      setSessionStarted(true);
    }
    setTimerRunning(true);
  };

  // Pause timer
  const handlePauseTimer = () => {
    setTimerRunning(false);
  };

  // End session
  const handleEndSession = () => {
    setTimerRunning(false);
    // Mark current interval as missed if not filled
    if (intervalData[currentIntervalIndex - 1] === null) {
      setIntervalData((prev) => {
        const newData = [...prev];
        newData[currentIntervalIndex - 1] = 'missed';
        return newData;
      });
    }
  };

  // Handle missed toast Y/N click
  const handleMissedToastAction = (value) => {
    const idx = missedToast.intervalIndex;
    if (idx === null) return;
    setIntervalData((prev) => {
      const newData = [...prev];
      newData[idx] = value;
      return newData;
    });
    if (missedToastTimerRef.current) clearTimeout(missedToastTimerRef.current);
    setMissedToast({ open: false, intervalIndex: null });
  };

  // Cleanup missed toast timer on unmount
  useEffect(() => {
    return () => {
      if (missedToastTimerRef.current) clearTimeout(missedToastTimerRef.current);
    };
  }, []);

  // Select cell value
  const handleSelectCell = (index, value) => {
    setIntervalData((prev) => {
      const newData = [...prev];
      newData[index] = value;
      return newData;
    });
  };

  // Clear cell
  const handleClearCell = (index) => {
    setIntervalData((prev) => {
      const newData = [...prev];
      newData[index] = null;
      return newData;
    });
  };

  // Jump to current interval
  const handleJumpToCurrent = () => {
    if (gridWrapperRef.current) {
      const currentCell = gridWrapperRef.current.querySelector(`[data-index="${currentIntervalIndex - 1}"]`);
      if (currentCell) {
        currentCell.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // New session — always open the full recording-setup page (Target Behavior /
  // Interval Type / Interval Length / Overall Duration controls) regardless of
  // viewMode. The grid view previously injected an inline-edit row instead,
  // but the therapists wanted a single consistent setup flow.
  const handleNewSession = () => {
    setShowRecording(true);
    setEditingSession(null);
    setSelectedBehavior('');
    setIntervalType('whole');
    setIntervalLength(30);
    setCustomIntervalLength('');
    setUseCustomInterval(false);
    setOverallDuration(10);
    setCustomDuration('');
    setUseCustomDuration(false);
    setTimerRunning(false);
    setSessionStarted(false);
    setSeconds(0);
    setCurrentIntervalIndex(1);
    setIntervalData([]);
    setOtherBehaviors([]);
    setNotes('');
  };

  // Cancel inline editing
  const handleCancelInline = () => {
    setInlineEditing(false);
  };

  // Save inline session (count-based)
  const handleSaveInlineSession = () => {
    const totalEntered = inlineWholeCount + inlinePartialCount + inlineNoneCount;
    if (totalEntered === 0 || !inlineBehavior) {
      return;
    }

    // Validate: counts cannot exceed total intervals
    if (totalEntered > inlineIntervalCount) {
      return;
    }

    const total = inlineIntervalCount;
    const missedCount = total - totalEntered;

    // Generate data array for compatibility
    const dataArray = [
      ...new Array(inlineWholeCount).fill('whole'),
      ...new Array(inlinePartialCount).fill('part'),
      ...new Array(inlineNoneCount).fill('none'),
      ...new Array(missedCount).fill('missed')
    ];

    const newSession = {
      id: Date.now(),
      date: new Date(),
      behavior: inlineBehavior,
      behaviorLabel: getBehaviorLabel(inlineBehavior),
      intervalType: inlineIntervalType,
      intervalLength: inlineIntervalLength,
      overallDuration: inlineOverallDuration,
      totalIntervals: total,
      data: dataArray,
      notes: inlineNotes,
      wholeCount: inlineWholeCount,
      partialCount: inlinePartialCount,
      noneCount: inlineNoneCount,
      missedCount,
      wholePercent: total > 0 ? Math.round((inlineWholeCount / total) * 100) : 0,
      partialPercent: total > 0 ? Math.round((inlinePartialCount / total) * 100) : 0,
      nonePercent: total > 0 ? Math.round((inlineNoneCount / total) * 100) : 0,
      missedPercent: total > 0 ? Math.round((missedCount / total) * 100) : 0,
      totalRecorded: totalEntered
    };

    // Update sessions state — the auto-save useEffect will persist to IndexedDB
    setSessions((prev) => [newSession, ...prev]);
    setInlineEditing(false);
  };

  // Load session
  const handleLoadSession = (session) => {
    setShowRecording(true);
    setEditingSession(session);
    setSelectedBehavior(session.behavior);
    setIntervalType(session.intervalType || 'whole');
    setIntervalLength(session.intervalLength);
    setUseCustomInterval(false);
    setOverallDuration(Math.ceil((session.totalIntervals * session.intervalLength) / 60));
    setUseCustomDuration(false);
    if (session.intervalType === 'momentary' && typeof session.momentSecond === 'number') {
      setMomentSecond(session.momentSecond);
    }
    setTimerRunning(false);
    setSessionStarted(true);
    setSeconds(0);
    setCurrentIntervalIndex(1);
    setIntervalData([...session.data]);
    setOtherBehaviors(alignOtherBehaviors(session.otherBehaviors, session.data.length, session.behavior));
    setNotes(session.notes || '');
  };

  // Delete session
  // eslint-disable-next-line no-unused-vars
  const handleDeleteClick = (session) => {
    setSessionToDelete(session);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (sessionToDelete) {
      setSessions((prev) => prev.filter((s) => s.id !== sessionToDelete.id));
    }
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  // Get behavior label
  const getBehaviorLabel = (value) => {
    const behavior = TARGET_BEHAVIORS.find((b) => b.value === value);
    return behavior?.label || value;
  };

  // The other-behaviour fields saved on a session (aligned to `length` intervals,
  // never including the target behaviour itself).
  const otherBehaviorFields = (length) => {
    const aligned = alignOtherBehaviors(otherBehaviors, length, selectedBehavior);
    return { otherBehaviors: aligned, ...summarizeOtherBehaviors(aligned, getBehaviorLabel) };
  };

  // Behaviours already recorded in THIS therapy session (other than the recording
  // being edited) - marked in the Target Behaviour list so the next one is easy to pick.
  const recordedThisSession = new Set(
    ownIntervalRecordings(sessions, sessionId)
      .filter((rec) => !editingSession || rec.id !== editingSession.id)
      .map((rec) => rec.behavior)
      .filter(Boolean)
  );

  // Behaviours offered as "other" for an interval: every behaviour but the target.
  const otherBehaviorOptions = TARGET_BEHAVIORS.filter((b) => b.value !== selectedBehavior);

  const toggleOtherBehavior = (index, value) => {
    setOtherBehaviors((prev) => {
      const next = alignOtherBehaviors(prev, intervalData.length);
      const cell = next[index];
      next[index] = cell.includes(value) ? cell.filter((v) => v !== value) : [...cell, value];
      return next;
    });
  };

  // Grid view summary calculations
  const gridSummary = useMemo(() => {
    const totalSessions = sessions.length;
    const avgWhole = totalSessions > 0 ? Math.round(sessions.reduce((sum, s) => sum + s.wholePercent, 0) / totalSessions) : 0;
    const avgPartial = totalSessions > 0 ? Math.round(sessions.reduce((sum, s) => sum + s.partialPercent, 0) / totalSessions) : 0;
    const totalIntervals = sessions.reduce((sum, s) => sum + s.totalIntervals, 0);
    return { totalSessions, avgWhole, avgPartial, totalIntervals };
  }, [sessions]);

  // Sessions split for the two tabs:
  //   • Data Entry  → sessions recorded this calendar day (just-recorded
  //     in-memory entries OR records loaded from backend/IndexedDB whose
  //     date matches today). Therapists expect today's saves to show up
  //     here even after closing & reopening the modal.
  //   • Historical Data → records from previous days only.
  const currentSessionEntries = useMemo(() => sessions.filter((s) => !s._historical || isSameDayAsToday(s.date)), [sessions]);
  const historicalSessions = useMemo(() => sessions.filter((s) => s._historical && !isSameDayAsToday(s.date)), [sessions]);

  // Pagination for Historical Data — show in chunks of 5.
  const HISTORICAL_PAGE_SIZE = 5;
  const [historicalVisibleCount, setHistoricalVisibleCount] = useState(HISTORICAL_PAGE_SIZE);
  const visibleHistoricalSessions = useMemo(
    () => historicalSessions.slice(0, historicalVisibleCount),
    [historicalSessions, historicalVisibleCount]
  );

  // Data-entry list (replaces the old "today only" filter so the therapist
  // sees every recording they just made in this modal, regardless of date).
  const todaySessions = currentSessionEntries;

  // Handle grid tab change
  const handleGridTabChange = (event, newValue) => {
    setGridTabValue(newValue);
  };

  // Get interval summary breakdown
  const getIntervalBreakdown = (data) => {
    const whole = data.filter((v) => v === 'whole').length;
    const partial = data.filter((v) => v === 'part').length;
    const none = data.filter((v) => v === 'none').length;
    const missed = data.filter((v) => v === 'missed').length;
    return { whole, partial, none, missed };
  };

  // CSV data for export
  const csvData = useMemo(() => {
    return sessions.map((s) => ({
      date: format(new Date(s.date), 'yyyy-MM-dd HH:mm'),
      behavior: getBehaviorLabel(s.behavior),
      intervalType: s.intervalType === 'partial' ? 'Partial Interval' : 'Whole Interval',
      intervalLength: s.intervalLength,
      totalIntervals: s.totalIntervals,
      wholeCount: s.wholeCount || 0,
      wholePercent: s.wholePercent,
      partialCount: s.partialCount || 0,
      partialPercent: s.partialPercent,
      noneCount: s.noneCount || 0,
      nonePercent: s.nonePercent || 0,
      missedCount: s.missedCount || 0,
      missedPercent: s.missedPercent || 0,
      notes: s.notes || ''
    }));
  }, [sessions]); // eslint-disable-line react-hooks/exhaustive-deps

  // The recordings this sheet SAVES: the ones made in THIS therapy session - new
  // ones, plus earlier ones of the same session loaded back (re-opened sheet).
  // Other days' recordings are shown in Historical Data but never re-saved: older
  // saves embedded them in every row, so data grew and views listed them as part
  // of the session. Saved recordings carry their session id and no _historical.
  const ownRecordings = (list) => ownIntervalRecordings(list, sessionId);

  // Auto-save: notify parent when form data changes for debounced persistence
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Check if any meaningful data has been entered in THIS modal session.
    // Sessions loaded from the backend (`_historical: true`) don't count —
    // otherwise opening the modal would immediately notify the parent and the
    // "has data" green-tick badge would show without the therapist entering
    // anything.
    const hasRecordingData = intervalData.length > 0 && intervalData.some((v) => v !== null);
    const hasInlineData = inlineWholeCount + inlinePartialCount + inlineNoneCount > 0 && inlineBehavior;
    const hasNewSessions = sessions.some((s) => !s._historical);

    if (!hasRecordingData && !hasInlineData && !hasNewSessions) {
      return;
    }

    // Collect current form data matching the save structure
    // Build flat formData using the latest session or current recording data.
    // This ensures raw_data has top-level fields (behavior, data, wholeCount, etc.)
    // that both extractTrialMetrics and BehaviorIntervalView can read.
    const own = ownRecordings(sessions);
    const latestSession = own.length > 0 ? own[0] : null;
    const source = hasRecordingData
      ? {
          behavior: selectedBehavior,
          // The name as well as the value: a reference behaviour's value is its id.
          behaviorLabel: getBehaviorLabel(selectedBehavior),
          intervalType,
          intervalLength: actualIntervalLength,
          momentSecond: intervalType === 'momentary' ? momentSecond : null,
          totalIntervals: intervalData.length,
          data: [...intervalData],
          ...otherBehaviorFields(intervalData.length),
          wholeCount: summary.wholeCount,
          partialCount: summary.partialCount,
          noneCount: summary.noneCount,
          missedCount: summary.missedCount,
          wholePercent: summary.wholePct,
          partialPercent: summary.partialPct,
          nonePercent: summary.nonePct,
          missedPercent: summary.missedPct,
          totalRecorded: summary.totalRecorded
        }
      : latestSession;

    const formData = {
      // Flat top-level fields from the latest data (for metrics extraction and view)
      ...(source || {}),
      // This therapy session's recordings (a session can record several behaviours)
      sessions: own,
      // Keep notes at top level for the API payload notes extraction
      sessionNotes: hasRecordingData ? notes : latestSession?.notes || '',
      // Surface live recording state so the parent overflow modal can block
      // its outer Save button while an interval session is mid-recording —
      // saving before the inner "Save Session" runs would persist nothing.
      recordingInProgress: sessionStarted
    };

    if (onDataChange) onDataChange('behavior-interval', formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessions,
    intervalData,
    otherBehaviors,
    selectedBehavior,
    intervalType,
    momentSecond,
    notes,
    inlineBehavior,
    inlineIntervalType,
    inlineWholeCount,
    inlinePartialCount,
    inlineNoneCount,
    inlineNotes,
    inlineIntervalLength,
    inlineOverallDuration,
    sessionStarted
  ]);

  // Expose end+save action to the parent overflow modal. We reassign on each
  // render so the closure always reads the latest state — calling
  // actionsRef.current.endAndSaveCurrentRecording() ends the running session,
  // marks the unfilled current interval as missed, commits the session to
  // the sessions[] array (so the auto-save effect persists it), and resets
  // the recording view. Returns { saved: true|false, reason? } so the caller
  // can decide whether to surface a "nothing was recorded" notice.
  // Finish the open recording: stop the timer, mark the unfilled current interval
  // missed, add the recording to this session's list and persist it right away,
  // then return to the list. Used by "Done" and by the page's End Session.
  const finishRecording = () => {
    if (!sessionStarted) return { saved: false, reason: 'not-started' };

    // Mirror handleEndSession: stop timer + mark current interval missed
    // if the therapist hadn't filled it before ending.
    let finalData = intervalData;
    if (currentIntervalIndex >= 1 && intervalData[currentIntervalIndex - 1] === null) {
      finalData = [...intervalData];
      finalData[currentIntervalIndex - 1] = 'missed';
    }

    const recorded = finalData.filter((v) => v !== null && v !== 'missed').length;
    if (recorded === 0) {
      // Nothing usable to save — just tear down the recording view.
      setTimerRunning(false);
      setSessionStarted(false);
      setShowRecording(false);
      setIntervalData([]);
      setOtherBehaviors([]);
      setSelectedBehavior('');
      setNotes('');
      setEditingSession(null);
      return { saved: false, reason: 'no-data' };
    }

    const total = finalData.length;
    const wholeCount = finalData.filter((v) => v === 'whole').length;
    const partialCount = finalData.filter((v) => v === 'part').length;
    const noneCount = finalData.filter((v) => v === 'none').length;
    const missedCount = finalData.filter((v) => v === 'missed').length;

    const newSession = {
      id: editingSession?.id || Date.now(),
      date: new Date(),
      behavior: selectedBehavior,
      // The name as well as the value: a reference behaviour's value is its id.
      behaviorLabel: getBehaviorLabel(selectedBehavior),
      intervalType,
      intervalLength: actualIntervalLength,
      momentSecond: intervalType === 'momentary' ? momentSecond : null,
      totalIntervals: total,
      data: [...finalData],
      ...otherBehaviorFields(total),
      notes,
      wholeCount,
      partialCount,
      noneCount,
      missedCount,
      wholePercent: total > 0 ? Math.round((wholeCount / total) * 100) : 0,
      partialPercent: total > 0 ? Math.round((partialCount / total) * 100) : 0,
      nonePercent: total > 0 ? Math.round((noneCount / total) * 100) : 0,
      missedPercent: total > 0 ? Math.round((missedCount / total) * 100) : 0,
      totalRecorded: wholeCount + partialCount + noneCount
    };

    const nextSessions = editingSession ? sessions.map((s) => (s.id === editingSession.id ? newSession : s)) : [newSession, ...sessions];

    setSessions(nextSessions);

    // The auto-save useEffect runs after the next render, but the parent
    // is about to close the modal — which unmounts this component before
    // the effect fires. Push the freshly-committed sessions through
    // onDataChange here so triggerAutoSave persists them to IndexedDB
    // before we tear down.
    if (onDataChange) {
      onDataChange('behavior-interval', {
        ...newSession,
        sessions: ownRecordings(nextSessions),
        sessionNotes: newSession.notes || '',
        recordingInProgress: false
      });
    }

    // Reset the recorder and return to the list.
    setIntervalData([]);
    setOtherBehaviors([]);
    setSelectedBehavior('');
    setNotes('');
    setTimerRunning(false);
    setSessionStarted(false);
    setShowRecording(false);
    setEditingSession(null);
    return { saved: true };
  };

  // "Done": the taps are already auto-saved as the open recording; this adds the
  // recording to the session (nothing recorded -> just closes). Replaces the old
  // Save Session / Save & record another / Close trio - Close could leave taps
  // behind that the next New Recording then dropped.
  const handleDone = () => {
    const result = finishRecording();
    if (result.reason === 'not-started') {
      setShowRecording(false);
      setEditingSession(null);
    }
  };

  // "New Behaviour": add this recording to the session (already auto-saved as the
  // open recording) and start a fresh one for the next behaviour.
  const handleNextBehaviour = () => {
    finishRecording();
    handleNewSession();
  };

  if (actionsRef) {
    actionsRef.current = {
      hasActiveRecording: () => sessionStarted,
      endAndSaveCurrentRecording: finishRecording
    };
  }

  // Render interval cell
  const renderIntervalCell = (index) => {
    const value = intervalData[index];
    const isCurrent = index === currentIntervalIndex - 1 && sessionStarted && !value;

    // Determine Y value based on interval type
    const yValue = intervalType === 'partial' ? 'part' : 'whole';
    const yColor = intervalType === 'partial' ? theme.palette.warning.main : theme.palette.success.main;
    const yLabel = intervalType === 'partial' ? 'P' : 'W';

    // Selected state (Y = whole/part, N = none)
    if (value === 'whole' || value === 'part' || value === 'none') {
      const isYes = value === 'whole' || value === 'part';
      const bg = isYes ? yColor : SOFT_RED;
      const label = isYes ? yLabel : 'N';

      return (
        <Box
          data-index={index}
          onClick={() => handleClearCell(index)}
          sx={{
            aspectRatio: '1',
            bgcolor: bg,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative',
            border: isCurrent ? `3px solid ${theme.palette.secondary.main}` : 'none',
            boxShadow: isCurrent ? `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.3)}` : 'none',
            transition: 'transform 0.1s',
            '&:hover': {
              transform: 'scale(0.95)',
              opacity: 0.9
            }
          }}
        >
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: 2,
              left: 4,
              fontSize: '0.6rem',
              fontWeight: 600,
              color: 'rgba(255,255,255,0.7)'
            }}
          >
            {index + 1}
          </Typography>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
            {label}
          </Typography>
        </Box>
      );
    }

    // Missed state
    if (value === 'missed') {
      return (
        <Box
          data-index={index}
          onClick={() => handleClearCell(index)}
          sx={{
            aspectRatio: '1',
            bgcolor: alpha(SOFT_RED, 0.08),
            borderRadius: 1.5,
            border: `2px dashed ${alpha(SOFT_RED, 0.3)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative'
          }}
        >
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: 2,
              left: 4,
              fontSize: '0.6rem',
              fontWeight: 600,
              color: alpha(SOFT_RED, 0.5)
            }}
          >
            {index + 1}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: alpha(SOFT_RED, 0.4),
              fontWeight: 700,
              fontSize: '0.65rem',
              letterSpacing: 0.5,
              transform: 'rotate(-45deg)',
              textTransform: 'uppercase'
            }}
          >
            MISSED
          </Typography>
        </Box>
      );
    }

    // Default state - Y (top-left) / N (bottom-right) split by diagonal
    return (
      <Box
        data-index={index}
        sx={{
          aspectRatio: '1',
          bgcolor: 'background.paper',
          borderRadius: 1.5,
          border: isCurrent ? `3px solid ${theme.palette.secondary.main}` : `2px solid ${theme.palette.divider}`,
          boxShadow: isCurrent ? `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.3)}` : 'none',
          position: 'relative',
          overflow: 'hidden',
          animation: isCurrent ? 'pulse 1.5s infinite' : 'none',
          '@keyframes pulse': {
            '0%, 100%': { boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.3)}` },
            '50%': { boxShadow: `0 0 0 6px ${alpha(theme.palette.secondary.main, 0.15)}` }
          }
        }}
      >
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            top: 2,
            left: 4,
            fontSize: '0.6rem',
            fontWeight: 600,
            color: 'text.secondary',
            zIndex: 15
          }}
        >
          {index + 1}
        </Typography>

        {/* Y area - top left triangle */}
        <Box
          onClick={() => handleSelectCell(index, yValue)}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            clipPath: 'polygon(0 0, 100% 0, 0 100%)',
            cursor: 'pointer',
            transition: 'background 0.15s',
            '&:hover': {
              bgcolor: alpha(yColor, 0.15),
              '& .label': { color: alpha(yColor, 0.8) }
            }
          }}
        >
          <Typography
            className="label"
            sx={{
              position: 'absolute',
              top: '22%',
              left: '22%',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: alpha(yColor, 0.3)
            }}
          >
            Y
          </Typography>
        </Box>

        {/* N area - bottom right triangle */}
        <Box
          onClick={() => handleSelectCell(index, 'none')}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100%',
            height: '100%',
            clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
            cursor: 'pointer',
            transition: 'background 0.15s',
            '&:hover': {
              bgcolor: alpha(SOFT_RED, 0.15),
              '& .label': { color: alpha(SOFT_RED, 0.8) }
            }
          }}
        >
          <Typography
            className="label"
            sx={{
              position: 'absolute',
              bottom: '22%',
              right: '22%',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: alpha(SOFT_RED, 0.3)
            }}
          >
            N
          </Typography>
        </Box>

        {/* Diagonal line */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 10
          }}
        >
          <svg width="100%" height="100%" style={{ display: 'block' }}>
            <line x1="100%" y1="0" x2="0" y2="100%" stroke={theme.palette.divider} strokeWidth="2" vectorEffect="non-scaling-stroke" />
          </svg>
        </Box>
      </Box>
    );
  };

  // 2nd chip under each interval cell: the target behaviour chosen for this
  // recording, with any ADDED behaviours sitting beside it. Added behaviours can
  // be removed with their delete (x) icon.
  const renderBehaviorChip = (index) => {
    if (!selectedBehavior) return null;
    const added = (otherBehaviors[index] || []).filter((v) => v !== selectedBehavior);
    return (
      <Box
        sx={{
          mt: 0.5,
          width: '100%',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          p: 0.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25
        }}
      >
        <Chip
          size="small"
          label={getBehaviorLabel(selectedBehavior)}
          color="primary"
          variant="outlined"
          sx={{ height: 22, width: '100%', fontSize: '0.7rem', '& .MuiChip-label': { flex: 1, textAlign: 'center', px: 0.5 } }}
        />
        {added.map((v) => (
          <Chip
            key={v}
            size="small"
            label={getBehaviorLabel(v)}
            color="info"
            onDelete={() => toggleOtherBehavior(index, v)}
            sx={{
              height: 22,
              width: '100%',
              fontSize: '0.7rem',
              '& .MuiChip-label': { flex: 1, textAlign: 'center', px: 0.5 },
              '& .MuiChip-deleteIcon': { fontSize: 13, ml: 0 }
            }}
          />
        ))}
      </Box>
    );
  };

  // 3rd chip: "Add behavior" control under each interval cell. Opens the shared
  // menu to tick behaviours that occurred in that interval; the ticked ones then
  // sit inside the 2nd chip. Hidden when the child has no behaviour besides the target.
  const renderOtherBehaviorPicker = (index) => {
    if (otherBehaviorOptions.length === 0) return null;
    const picked = (otherBehaviors[index] || []).filter((v) => v !== selectedBehavior);
    const names = picked.map(getBehaviorLabel).join(', ');
    const label = intl.formatMessage({ id: 'form.behaviorInterval.addBehavior', defaultMessage: '+ Add behavior' });
    const hint = intl.formatMessage({
      id: 'form.behaviorInterval.addOtherHint',
      defaultMessage: 'Add other behaviours seen in this interval'
    });
    return (
      <Tooltip title={names || hint}>
        <Box
          component="button"
          type="button"
          onClick={(e) => setOtherMenu({ anchorEl: e.currentTarget, index })}
          aria-label={intl.formatMessage(
            { id: 'form.behaviorInterval.otherInInterval', defaultMessage: 'Other behaviours in interval {n}' },
            { n: index + 1 }
          )}
          sx={{
            mt: 0.5,
            width: '100%',
            border: 1,
            borderColor: picked.length ? 'info.main' : 'divider',
            borderRadius: 1,
            bgcolor: picked.length ? alpha(theme.palette.info.main, 0.1) : 'background.paper',
            color: picked.length ? 'info.dark' : 'text.secondary',
            fontSize: '0.7rem',
            fontWeight: 600,
            lineHeight: 1.3,
            px: 0.5,
            py: 0.5,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            '&:hover': { borderColor: 'info.main' }
          }}
        >
          {label}
        </Box>
      </Tooltip>
    );
  };

  // Render session card
  const renderSessionCard = (session) => {
    const totalDuration = Math.round((session.intervalLength * session.totalIntervals) / 60);

    return (
      <Paper
        key={session.id}
        elevation={0}
        sx={{
          p: 2.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: theme.shadows[2]
          }
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Box sx={{ flex: 1 }}>
            {/* Header row with date and behavior */}
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" fontWeight={600}>
                {format(new Date(session.date), 'MMM dd, yyyy — h:mm a')}
              </Typography>
              {session.behavior && <Chip label={getBehaviorLabel(session.behavior)} size="small" variant="outlined" />}
              <Chip
                label={
                  session.intervalType === 'partial'
                    ? intl.formatMessage({ id: 'form.behaviorInterval.partialInterval' })
                    : intl.formatMessage({ id: 'form.behaviorInterval.wholeInterval' })
                }
                size="small"
                color={session.intervalType === 'partial' ? 'warning' : 'success'}
                variant="outlined"
              />
            </Stack>

            {/* Session details row */}
            <Stack direction="row" spacing={3} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary">
                <strong>{session.intervalLength}s</strong> {intl.formatMessage({ id: 'form.behaviorInterval.interval' }).toLowerCase()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{totalDuration}</strong> {intl.formatMessage({ id: 'common.minutes' })}{' '}
                {intl.formatMessage({ id: 'form.behaviorInterval.duration' }).toLowerCase()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{session.totalIntervals}</strong> {intl.formatMessage({ id: 'form.behaviorInterval.interval' }).toLowerCase()}s
              </Typography>
            </Stack>

            {/* Summary chips row */}
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              <Chip label={`Whole: ${session.wholePercent}%`} size="small" color="success" variant="filled" />
              <Chip label={`Partial: ${session.partialPercent}%`} size="small" color="warning" variant="filled" />
              <Chip label={`None: ${session.nonePercent || 0}%`} size="small" sx={{ bgcolor: SOFT_RED, color: 'white' }} variant="filled" />
              <Chip
                label={`Missed: ${session.missedPercent || 0}%`}
                size="small"
                sx={{ bgcolor: alpha(SOFT_RED, 0.15), color: SOFT_RED, border: `1px dashed ${SOFT_RED}` }}
                variant="filled"
              />
            </Stack>
            <OtherBehaviorsSummary record={session} labelOf={getBehaviorLabel} sx={{ mt: 1 }} />
          </Box>

          <Button variant="contained" size="small" startIcon={<EyeOutlined />} onClick={() => handleLoadSession(session)}>
            {intl.formatMessage({ id: 'form.behaviorInterval.view' })}
          </Button>
        </Stack>
      </Paper>
    );
  };

  // Recording view
  if (showRecording) {
    // Behaviours already recorded in THIS therapy session — a read-only preview so the
    // collector can see what was captured earlier while recording another behaviour
    // (mirrors how injury-report / ST-OT surface already-collected data in a session).
    // Shown above the grid before Start; moved to the bottom once recording starts so
    // the interval grid and controls take priority.
    const previousRecordings = currentSessionEntries.filter((s) => !editingSession || s.id !== editingSession.id);
    const recordedThisSessionPreview = previousRecordings.length > 0 && (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          bgcolor: alpha(SOFT_RED, 0.04),
          border: `1px solid ${alpha(SOFT_RED, 0.3)}`,
          borderRadius: 2
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: SOFT_RED, mb: 1.5 }}>
          {intl.formatMessage({
            id: 'form.behaviorInterval.alreadyRecordedThisSession',
            defaultMessage: 'Already recorded this session'
          })}
        </Typography>
        <Stack spacing={2}>{previousRecordings.map((session) => renderSessionCard(session))}</Stack>
      </Paper>
    );

    return (
      <Stack spacing={3}>
        {/* Recording Header */}
        <MainCard>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="h5" fontWeight={600}>
              {editingSession
                ? intl.formatMessage({ id: 'form.behaviorInterval.editSession' })
                : intl.formatMessage({ id: 'form.behaviorInterval.newRecording' })}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {intl.formatMessage({
                  id: 'form.behaviorInterval.autoSavedHint',
                  defaultMessage: 'Every tap is saved automatically'
                })}
              </Typography>
              {/* Close only while nothing is recorded - then it can lose nothing. Once
                  taps exist, New Behaviour (or End Session) keeps them. */}
              {summary.totalRecorded === 0 && (
                <Button variant="text" color="inherit" onClick={handleDone} disabled={saving}>
                  {intl.formatMessage({ id: 'common.close' })}
                </Button>
              )}
              <Button
                variant="contained"
                color="success"
                startIcon={<PlusOutlined />}
                onClick={handleNextBehaviour}
                disabled={saving || summary.totalRecorded === 0}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {intl.formatMessage({ id: 'form.behaviorInterval.nextBehaviour', defaultMessage: 'New Behaviour' })}
              </Button>
            </Stack>
          </Stack>
        </MainCard>

        {/* Settings */}
        <Paper sx={{ p: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{intl.formatMessage({ id: 'form.behaviorInterval.targetBehavior' })}</InputLabel>
                <Select
                  value={selectedBehavior}
                  label={intl.formatMessage({ id: 'form.behaviorInterval.targetBehavior' })}
                  onChange={(e) => setSelectedBehavior(e.target.value)}
                  disabled={sessionStarted}
                >
                  <MenuItem value="">{intl.formatMessage({ id: 'form.behaviorInterval.selectBehavior' })}</MenuItem>
                  {TARGET_BEHAVIORS.map((b) => (
                    <MenuItem key={b.value} value={b.value}>
                      {b.label}
                      {recordedThisSession.has(b.value) && (
                        <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1, fontWeight: 600 }}>
                          {intl.formatMessage({ id: 'form.behaviorInterval.alreadyRecorded', defaultMessage: '✓ recorded this session' })}
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{intl.formatMessage({ id: 'form.behaviorInterval.intervalType' })}</InputLabel>
                <Select
                  value={intervalType}
                  label={intl.formatMessage({ id: 'form.behaviorInterval.intervalType' })}
                  onChange={(e) => setIntervalType(e.target.value)}
                  disabled={sessionStarted}
                >
                  {INTERVAL_TYPES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              {useCustomInterval ? (
                <TextField
                  fullWidth
                  size="small"
                  label={intl.formatMessage({ id: 'form.behaviorInterval.intervalLengthSeconds' })}
                  type="number"
                  value={customIntervalLength}
                  onChange={(e) => setCustomIntervalLength(e.target.value)}
                  onBlur={() => {
                    if (!customIntervalLength || parseInt(customIntervalLength) < 1) {
                      setUseCustomInterval(false);
                      setIntervalLength(30);
                    }
                  }}
                  disabled={sessionStarted}
                  inputProps={{ min: 1 }}
                />
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel>{intl.formatMessage({ id: 'form.behaviorInterval.intervalLength' })}</InputLabel>
                  <Select
                    value={intervalLength}
                    label={intl.formatMessage({ id: 'form.behaviorInterval.intervalLength' })}
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        setUseCustomInterval(true);
                        setCustomIntervalLength('');
                      } else {
                        setIntervalLength(e.target.value);
                      }
                    }}
                    disabled={sessionStarted}
                  >
                    {INTERVAL_LENGTHS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                    <MenuItem value="custom">{intl.formatMessage({ id: 'form.behaviorInterval.custom' })}</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              {useCustomDuration ? (
                <TextField
                  fullWidth
                  size="small"
                  label={intl.formatMessage({ id: 'form.behaviorInterval.overallDurationMinutes' })}
                  type="number"
                  value={customDuration}
                  onChange={(e) => setCustomDuration(e.target.value)}
                  onBlur={() => {
                    if (!customDuration || parseInt(customDuration) < 1) {
                      setUseCustomDuration(false);
                      setOverallDuration(10);
                    }
                  }}
                  disabled={sessionStarted}
                  inputProps={{ min: 1 }}
                />
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel>{intl.formatMessage({ id: 'form.behaviorInterval.overallDuration' })}</InputLabel>
                  <Select
                    value={overallDuration}
                    label={intl.formatMessage({ id: 'form.behaviorInterval.overallDuration' })}
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        setUseCustomDuration(true);
                        setCustomDuration('');
                      } else {
                        setOverallDuration(e.target.value);
                      }
                    }}
                    disabled={sessionStarted}
                  >
                    {DURATION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                    <MenuItem value="custom">{intl.formatMessage({ id: 'form.behaviorInterval.custom' })}</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Grid>
          </Grid>

          {intervalType === 'momentary' && (
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                px: 2.5,
                py: 2,
                bgcolor: alpha(theme.palette.info.main, 0.05),
                border: `1px dashed ${alpha(theme.palette.info.main, 0.3)}`,
                borderRadius: 1.5
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {intl.formatMessage({
                    id: 'form.behaviorInterval.momentSecond',
                    defaultMessage: 'Sample at second'
                  })}
                </Typography>
                <Chip size="small" color="info" variant="outlined" label={`${momentSecond}s of ${actualIntervalLength}s`} />
              </Stack>
              <Slider
                value={momentSecond}
                min={0}
                max={actualIntervalLength}
                step={1}
                marks={[
                  { value: 0, label: '0s' },
                  { value: actualIntervalLength, label: `${actualIntervalLength}s` }
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}s`}
                onChange={(_, v) => setMomentSecond(Array.isArray(v) ? v[0] : v)}
                disabled={sessionStarted}
                sx={{ mx: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {intl.formatMessage({
                  id: 'form.behaviorInterval.momentaryHelp',
                  defaultMessage:
                    'Record whether the behavior occurs at this exact moment within each interval. Default is the end of the interval.'
                })}
              </Typography>
            </Paper>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }} flexWrap="wrap" gap={2}>
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1,
                bgcolor: alpha(theme.palette.info.main, 0.08),
                borderRadius: 1.5
              }}
            >
              <Typography variant="body2" color="text.secondary">
                This will create <strong style={{ color: theme.palette.primary.main }}>{totalIntervals} intervals</strong>
              </Typography>
            </Paper>
            {!sessionStarted ? (
              <Button variant="contained" color="success" startIcon={<PlayCircleOutlined />} onClick={handleStartTimer}>
                {intl.formatMessage({ id: 'form.behaviorInterval.start' })}
              </Button>
            ) : (
              <Chip label={intl.formatMessage({ id: 'form.behaviorInterval.sessionStarted' })} color="success" />
            )}
          </Stack>
        </Paper>

        {/* Before Start: show the already-recorded preview here, above the grid. */}
        {!sessionStarted && recordedThisSessionPreview}

        {/* Instructions */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            bgcolor: alpha(theme.palette.info.main, 0.04),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            borderRadius: 2
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} color="info.main" sx={{ mb: 1 }}>
            How to Record — {intervalType === 'partial' ? 'Partial' : intervalType === 'momentary' ? 'Momentary Time Sampling' : 'Whole'}{' '}
            {intervalType === 'momentary' ? '' : 'Interval'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {intervalType === 'momentary' ? (
              <>
                Click <strong>Y</strong> (top-left) or <strong>N</strong> (bottom-right) in each cell to record whether the behavior was
                occurring at the <strong>{momentSecond}-second</strong> mark of that interval. Click a filled cell to clear it.
              </>
            ) : (
              <>
                Click <strong>Y</strong> (top-left) or <strong>N</strong> (bottom-right) in each cell to record whether the behavior
                occurred. Click a filled cell to clear it.
              </>
            )}
          </Typography>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: intervalType === 'partial' ? 'warning.main' : intervalType === 'momentary' ? 'info.main' : 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>Y</Typography>
              </Box>
              <Typography variant="body2">
                <strong>Yes ({intervalType === 'partial' ? 'P' : intervalType === 'momentary' ? 'M' : 'W'}):</strong>{' '}
                {intervalType === 'partial'
                  ? 'Behavior occurred for PART of the interval'
                  : intervalType === 'momentary'
                    ? `Behavior was occurring AT the ${momentSecond}-second mark`
                    : 'Behavior occurred the ENTIRE interval'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: SOFT_RED,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>N</Typography>
              </Box>
              <Typography variant="body2">
                <strong>No (N):</strong> Behavior did NOT occur
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        {/* Timer */}
        {sessionStarted && (
          <Paper
            elevation={0}
            sx={{
              px: 2.5,
              py: 1.5,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              borderRadius: 2
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={1.5}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                <Box
                  sx={{
                    px: 2,
                    py: 0.75,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '1.75rem',
                      fontWeight: 600,
                      color: 'primary.main',
                      letterSpacing: 2
                    }}
                  >
                    {formatTime(seconds)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {intl.formatMessage({ id: 'form.behaviorInterval.currentInterval' })}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    {currentIntervalIndex}{' '}
                    <Typography component="span" variant="body2" color="text.secondary">
                      / {totalIntervals}
                    </Typography>
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' }, '& > *': { flex: { xs: 1, sm: 'none' } } }}>
                {timerRunning ? (
                  <Button
                    variant="contained"
                    sx={{ bgcolor: 'warning.main', '&:hover': { bgcolor: 'warning.dark' } }}
                    startIcon={<PauseCircleOutlined />}
                    onClick={handlePauseTimer}
                  >
                    {intl.formatMessage({ id: 'form.behaviorInterval.pause' })}
                  </Button>
                ) : (
                  <Button variant="contained" color="success" startIcon={<PlayCircleOutlined />} onClick={handleStartTimer}>
                    {intl.formatMessage({ id: 'form.behaviorInterval.resume' })}
                  </Button>
                )}
                <Button variant="contained" color="error" startIcon={<StopOutlined />} onClick={handleEndSession}>
                  {intl.formatMessage({ id: 'form.behaviorInterval.end' })}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Data Grid */}
        <Paper sx={{ p: { xs: 1.5, sm: 3 }, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap gap={1} sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              {intl.formatMessage({ id: 'form.behaviorInterval.intervalGrid' })}
            </Typography>
            {sessionStarted && (
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                startIcon={<DownOutlined />}
                onClick={handleJumpToCurrent}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {intl.formatMessage({ id: 'form.behaviorInterval.jumpToCurrent' })}
              </Button>
            )}
          </Stack>

          <Box
            ref={gridWrapperRef}
            sx={{
              maxHeight: { xs: '60vh', sm: 400 },
              overflowY: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.5,
              p: { xs: 1, sm: 1.5 },
              bgcolor: 'grey.50'
            }}
          >
            {intervalData.length > 0 ? (
              <Box
                sx={{
                  display: 'grid',
                  // Finger-sized cells: 3 per row on phones, 6 on tablets, 8 / 10 wider.
                  gridTemplateColumns: {
                    xs: 'repeat(3, minmax(0, 1fr))',
                    sm: 'repeat(6, minmax(0, 1fr))',
                    md: 'repeat(8, minmax(0, 1fr))',
                    lg: 'repeat(10, minmax(0, 1fr))'
                  },
                  alignItems: 'start',
                  gap: 1
                }}
              >
                {intervalData.map((_, index) => (
                  <Box key={index}>
                    {renderIntervalCell(index)}
                    {renderBehaviorChip(index)}
                    {renderOtherBehaviorPicker(index)}
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body1" color="text.secondary">
                  Click <strong style={{ color: theme.palette.success.main }}>Start</strong> to begin recording
                </Typography>
              </Box>
            )}
          </Box>

          {/* One shared menu for the per-interval "other behaviours" picker. */}
          <Menu
            anchorEl={otherMenu.anchorEl}
            open={Boolean(otherMenu.anchorEl)}
            onClose={() => setOtherMenu({ anchorEl: null, index: null })}
            slotProps={{ paper: { sx: { maxHeight: 320, minWidth: 220 } } }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block' }}>
              {intl.formatMessage(
                { id: 'form.behaviorInterval.otherInInterval', defaultMessage: 'Other behaviours in interval {n}' },
                { n: (otherMenu.index ?? 0) + 1 }
              )}
            </Typography>
            {otherBehaviorOptions.map((b) => {
              const checked = otherMenu.index !== null && (otherBehaviors[otherMenu.index] || []).includes(b.value);
              return (
                <MenuItem key={b.value} dense onClick={() => toggleOtherBehavior(otherMenu.index, b.value)}>
                  <Checkbox size="small" checked={checked} sx={{ p: 0.5, mr: 1 }} />
                  <ListItemText primary={b.label} />
                </MenuItem>
              );
            })}
          </Menu>

          {/* Legend */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 3 }}
            sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}
            flexWrap="wrap"
            useFlexGap
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{ width: 18, height: 18, borderRadius: 0.75, bgcolor: intervalType === 'partial' ? 'warning.main' : 'success.main' }}
              />
              <Typography variant="caption" color="text.secondary">
                <strong>Yes ({intervalType === 'partial' ? 'P' : 'W'})</strong> -{' '}
                {intervalType === 'partial' ? 'Behavior occurred for part of the interval' : 'Behavior occurred throughout entire interval'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box sx={{ width: 18, height: 18, borderRadius: 0.75, bgcolor: SOFT_RED }} />
              <Typography variant="caption" color="text.secondary">
                <strong>No (N)</strong> - Behavior did not occur
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: 0.75,
                  bgcolor: alpha(SOFT_RED, 0.08),
                  border: `2px dashed ${alpha(SOFT_RED, 0.3)}`
                }}
              />
              <Typography variant="caption" color="text.secondary">
                <strong>Missed</strong> - Interval was not recorded in time
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        {/* Overall Summary */}
        <Paper sx={{ p: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={600}>
              {intl.formatMessage({ id: 'form.behaviorInterval.overallSummary' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Intervals: <strong>{summary.totalIntervalCount}</strong>
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            {/* Yes (Whole or Partial based on interval type) */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: alpha(intervalType === 'partial' ? theme.palette.warning.main : theme.palette.success.main, 0.08),
                  textAlign: 'center'
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: intervalType === 'partial' ? 'warning.main' : 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1.5
                  }}
                >
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>
                    {intervalType === 'partial' ? 'P' : 'W'}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color={intervalType === 'partial' ? 'warning.main' : 'success.main'}>
                  {intervalType === 'partial' ? summary.partialPct : summary.wholePct}%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {intervalType === 'partial' ? summary.partialCount : summary.wholeCount} of {summary.totalIntervalCount} intervals
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Yes — {intervalType === 'partial' ? 'Partial Interval' : 'Whole Interval'}
                </Typography>
              </Paper>
            </Grid>

            {/* No */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: alpha(SOFT_RED, 0.08),
                  textAlign: 'center'
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: SOFT_RED,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1.5
                  }}
                >
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>N</Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: SOFT_RED }}>
                  {summary.nonePct}%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {summary.noneCount} of {summary.totalIntervalCount} intervals
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  No — Behavior Not Observed
                </Typography>
              </Paper>
            </Grid>

            {/* Missed */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: alpha(SOFT_RED, 0.04),
                  border: `2px dashed ${alpha(SOFT_RED, 0.25)}`,
                  textAlign: 'center'
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: alpha(SOFT_RED, 0.15),
                    border: `2px dashed ${alpha(SOFT_RED, 0.4)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1.5
                  }}
                >
                  <Typography sx={{ color: alpha(SOFT_RED, 0.7), fontWeight: 700, fontSize: '0.75rem' }}>
                    {intl.formatMessage({ id: 'dataCollection.intervalLabel.missed' })}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: alpha(SOFT_RED, 0.7) }}>
                  {summary.missedPct}%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {summary.missedCount} of {summary.totalIntervalCount} intervals
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Missed
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* OTHER BEHAVIOURS that occurred alongside the target (ticked per interval
              under the grid). Informational: the W / N / Missed figures above are
              for the target behaviour only. % = share of all intervals. */}
          {(() => {
            const total = summary.totalIntervalCount;
            const rows = {};
            otherBehaviors.forEach((cell, i) =>
              (cell || []).forEach((v) => {
                if (v && v !== selectedBehavior) (rows[v] = rows[v] || []).push(i + 1);
              })
            );
            const entries = Object.entries(rows).sort((a, b) => b[1].length - a[1].length);
            if (entries.length === 0) return null;
            return (
              <Box sx={{ mt: 3, pt: 2.5, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {intl.formatMessage({ id: 'form.behaviorInterval.otherBehaviorsTitle', defaultMessage: 'Other behaviours observed' })}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                  {intl.formatMessage({
                    id: 'form.behaviorInterval.otherBehaviorsNote',
                    defaultMessage: 'Recorded alongside the target behaviour; not included in the figures above.'
                  })}
                </Typography>
                <Stack spacing={1.25}>
                  {entries.map(([v, list]) => {
                    const pct = total > 0 ? Math.round((list.length / total) * 100) : 0;
                    return (
                      <Box key={v}>
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
                          <Typography variant="body2" fontWeight={600}>
                            {getBehaviorLabel(v)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>{pct}%</strong> · {list.length} of {total} intervals
                          </Typography>
                        </Stack>
                        <Box sx={{ mt: 0.5, height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.info.main, 0.12) }}>
                          <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 3, bgcolor: 'info.main' }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {intl.formatMessage(
                            { id: 'form.behaviorInterval.otherInIntervals', defaultMessage: 'Intervals {list}' },
                            { list: list.join(', ') }
                          )}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            );
          })()}
        </Paper>

        {/* Notes */}
        <Paper sx={{ p: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            {intl.formatMessage({ id: 'form.behaviorInterval.observerNotes' })}
          </Typography>
          <SpeechTextField
            fullWidth
            multiline
            rows={3}
            placeholder={intl.formatMessage({ id: 'form.behaviorInterval.observerNotesPlaceholder' })}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Paper>

        {/* After Start: the already-recorded preview moves to the bottom so the grid leads. */}
        {sessionStarted && recordedThisSessionPreview}

        {/* Missed interval toast with Y/N action buttons */}
        <Snackbar
          open={missedToast.open}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={() => setMissedToast({ open: false, intervalIndex: null })}
        >
          <Paper
            elevation={6}
            sx={{
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              bgcolor: 'background.paper',
              border: `2px solid ${alpha(SOFT_RED, 0.4)}`,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              minWidth: 320
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: alpha(SOFT_RED, 0.12),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <ClockCircleOutlined style={{ color: SOFT_RED, fontSize: '1.1rem' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              {(() => {
                const idx = missedToast.intervalIndex;
                const isMomentarySample = intervalType === 'momentary' && idx !== null && intervalData[idx] === null;
                return (
                  <>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {isMomentarySample
                        ? `Interval #${idx + 1} — sample at ${momentSecond}s`
                        : `Interval #${idx !== null ? idx + 1 : ''} missed!`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isMomentarySample ? 'Is the behavior occurring right now?' : 'Did the behavior occur?'}
                    </Typography>
                  </>
                );
              })()}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                sx={{
                  minWidth: 44,
                  bgcolor: intervalType === 'partial' ? 'warning.main' : intervalType === 'momentary' ? 'info.main' : 'success.main',
                  '&:hover': {
                    bgcolor: intervalType === 'partial' ? 'warning.dark' : intervalType === 'momentary' ? 'info.dark' : 'success.dark'
                  }
                }}
                onClick={() => handleMissedToastAction(intervalType === 'partial' ? 'part' : 'whole')}
              >
                Y
              </Button>
              <Button
                variant="contained"
                size="small"
                sx={{
                  minWidth: 44,
                  bgcolor: SOFT_RED,
                  '&:hover': { bgcolor: '#d32f2f' }
                }}
                onClick={() => handleMissedToastAction('none')}
              >
                N
              </Button>
            </Stack>
          </Paper>
        </Snackbar>
      </Stack>
    );
  }

  // Individual View - Session list view
  if (viewMode === 'individual') {
    return (
      <Stack spacing={3}>
        <MainCard>
          {/* Wraps instead of squeezing: on a phone the title and button each stay on
              one line (the button never breaks into two lines). */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap gap={1.5}>
            <Typography variant="h5" sx={{ flex: '1 1 auto', minWidth: 0 }}>
              {intl.formatMessage({ id: 'form.behaviorInterval.intervalRecordingSessions' })}
            </Typography>
            <Button
              variant="contained"
              startIcon={<PlusOutlined />}
              onClick={handleNewSession}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
            >
              {intl.formatMessage({ id: 'form.behaviorInterval.newSession' })}
            </Button>
          </Stack>
        </MainCard>

        {/* Session List — shows only recordings from this modal session.
            Historical entries live in the Historical Data tab (grid view). */}
        <Stack spacing={2}>
          {currentSessionEntries.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="h6" color="text.secondary">
                {intl.formatMessage({ id: 'form.behaviorInterval.noSessionsRecorded' })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Click &quot;+ New Session&quot; to start recording.
              </Typography>
            </Paper>
          ) : (
            currentSessionEntries.map((session) => renderSessionCard(session))
          )}
        </Stack>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>{intl.formatMessage({ id: 'form.behaviorInterval.deleteSession' })}</DialogTitle>
          <DialogContent>
            <DialogContentText>{intl.formatMessage({ id: 'form.behaviorInterval.deleteConfirm' })}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              {intl.formatMessage({ id: 'common.delete' })}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }

  // Grid View - Summary Table with Tabs
  const renderSessionRow = (session, isExpanded) => {
    const breakdown = getIntervalBreakdown(session.data);

    return (
      <>
        <TableRow
          hover
          sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'unset' : undefined } }}
          onClick={() => setExpandedRow(isExpanded ? null : session.id)}
        >
          <TableCell>
            <IconButton size="small">{isExpanded ? <UpOutlined /> : <DownOutlined />}</IconButton>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" fontWeight={600}>
              {format(new Date(session.date), 'MMM dd, yyyy')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {format(new Date(session.date), 'h:mm a')}
            </Typography>
          </TableCell>
          <TableCell>
            <Chip label={getBehaviorLabel(session.behavior)} size="small" variant="outlined" />
          </TableCell>
          <TableCell>
            <Chip
              label={
                session.intervalType === 'partial'
                  ? intl.formatMessage({ id: 'form.behaviorInterval.partial' })
                  : intl.formatMessage({ id: 'form.behaviorInterval.whole' })
              }
              size="small"
              color={session.intervalType === 'partial' ? 'warning' : 'success'}
              variant="outlined"
            />
          </TableCell>
          <TableCell align="center">
            <Typography variant="body2" fontWeight={600}>
              {session.totalIntervals}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant="body2" fontWeight={600}>
              {session.overallDuration || Math.round((session.totalIntervals * session.intervalLength) / 60)}{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                min
              </Typography>
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant="body2" fontWeight={600}>
              {session.intervalLength}{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                sec
              </Typography>
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant="body2" fontWeight={600} color="success.main">
              {session.wholeCount || breakdown.whole}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant="body2" fontWeight={600} color="warning.main">
              {session.partialCount || breakdown.partial}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant="body2" fontWeight={600} sx={{ color: SOFT_RED }}>
              {session.noneCount || breakdown.none}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant="body2" color="text.secondary">
              {session.missedCount || breakdown.missed}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Tooltip title={intl.formatMessage({ id: 'form.behaviorInterval.viewEdit' })}>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLoadSession(session);
                }}
              >
                <EyeOutlined />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
        {isExpanded && (
          <TableRow>
            <TableCell colSpan={12} sx={{ py: 0 }}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, my: 1 }}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      {intl.formatMessage({ id: 'form.behaviorInterval.intervalBreakdown' })}
                    </Typography>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: 'success.main' }} />
                        <Typography variant="body2">
                          Whole (W): <strong>{breakdown.whole}</strong>
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: 'warning.main' }} />
                        <Typography variant="body2">
                          Partial (P): <strong>{breakdown.partial}</strong>
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: SOFT_RED }} />
                        <Typography variant="body2">
                          None (N): <strong>{breakdown.none}</strong>
                        </Typography>
                      </Stack>
                      {breakdown.missed > 0 && (
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: 0.5,
                              bgcolor: alpha(SOFT_RED, 0.15),
                              border: `2px dashed ${alpha(SOFT_RED, 0.4)}`
                            }}
                          />
                          <Typography variant="body2">
                            Missed: <strong>{breakdown.missed}</strong>
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    {session.notes && (
                      <>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          {intl.formatMessage({ id: 'common.notes' })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {session.notes}
                        </Typography>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  // Render inline editing row for grid view (count-based entry like Excel)
  const renderInlineEditRow = () => {
    const totalEntered = inlineWholeCount + inlinePartialCount + inlineNoneCount;
    const exceedsTotal = totalEntered > inlineIntervalCount;
    const missedCount = exceedsTotal ? 0 : inlineIntervalCount - totalEntered;

    return (
      <TableRow
        sx={{
          bgcolor: exceedsTotal ? alpha(theme.palette.error.main, 0.08) : alpha(theme.palette.primary.main, 0.04)
        }}
      >
        <TableCell>
          <Chip label="NEW" size="small" color="primary" variant="outlined" />
        </TableCell>
        <TableCell>
          <Typography variant="subtitle2" fontWeight={600} color="primary.main">
            {format(new Date(), 'MMM dd, yyyy')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(), 'h:mm a')}
          </Typography>
        </TableCell>
        <TableCell>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select value={inlineBehavior} onChange={(e) => setInlineBehavior(e.target.value)} displayEmpty sx={{ fontSize: '0.8rem' }}>
              <MenuItem value="" disabled>
                {intl.formatMessage({ id: 'form.behaviorInterval.selectBehavior' })}
              </MenuItem>
              {TARGET_BEHAVIORS.map((b) => (
                <MenuItem key={b.value} value={b.value}>
                  {b.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        <TableCell>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select value={inlineIntervalType} onChange={(e) => setInlineIntervalType(e.target.value)} sx={{ fontSize: '0.8rem' }}>
              {INTERVAL_TYPES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" fontWeight={600} color="primary.main">
            {inlineIntervalCount}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
            <TextField
              size="small"
              type="number"
              value={inlineOverallDuration}
              onChange={(e) => setInlineOverallDuration(Math.max(1, parseInt(e.target.value) || 1))}
              sx={{ width: 50 }}
              inputProps={{ min: 1, max: 120 }}
            />
            <Typography variant="caption" color="text.secondary">
              min
            </Typography>
          </Stack>
        </TableCell>
        <TableCell align="center">
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
            <TextField
              size="small"
              type="number"
              value={inlineIntervalLength}
              onChange={(e) => setInlineIntervalLength(Math.max(1, parseInt(e.target.value) || 1))}
              sx={{ width: 50 }}
              inputProps={{ min: 1, max: 300 }}
            />
            <Typography variant="caption" color="text.secondary">
              sec
            </Typography>
          </Stack>
        </TableCell>
        <TableCell align="center">
          <TextField
            size="small"
            type="number"
            value={inlineWholeCount}
            onChange={(e) => setInlineWholeCount(Math.max(0, parseInt(e.target.value) || 0))}
            onFocus={(e) => e.target.select()}
            error={exceedsTotal}
            sx={{ width: 60 }}
            inputProps={{ min: 0 }}
          />
        </TableCell>
        <TableCell align="center">
          <TextField
            size="small"
            type="number"
            value={inlinePartialCount}
            onChange={(e) => setInlinePartialCount(Math.max(0, parseInt(e.target.value) || 0))}
            onFocus={(e) => e.target.select()}
            error={exceedsTotal}
            sx={{ width: 60 }}
            inputProps={{ min: 0 }}
          />
        </TableCell>
        <TableCell align="center">
          <TextField
            size="small"
            type="number"
            value={inlineNoneCount}
            onChange={(e) => setInlineNoneCount(Math.max(0, parseInt(e.target.value) || 0))}
            onFocus={(e) => e.target.select()}
            error={exceedsTotal}
            sx={{ width: 60 }}
            inputProps={{ min: 0 }}
          />
        </TableCell>
        <TableCell align="center">
          {exceedsTotal ? (
            <Typography variant="caption" color="error" fontWeight={600}>
              Exceeds by {totalEntered - inlineIntervalCount}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {missedCount}
            </Typography>
          )}
        </TableCell>
        <TableCell align="center">
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={exceedsTotal ? 'Total counts exceed intervals' : saving ? 'Saving...' : 'Save'}>
              <span>
                <IconButton
                  size="small"
                  color="success"
                  onClick={handleSaveInlineSession}
                  disabled={inlineBehavior === '' || totalEntered === 0 || exceedsTotal || saving}
                >
                  <SaveOutlined />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: 'common.cancel' })}>
              <IconButton size="small" color="error" onClick={handleCancelInline}>
                <CloseOutlined />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Stack spacing={3}>
      {/* Summary Statistics */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MainCard>
            <Stack spacing={0.5}>
              <Typography variant="h3" fontWeight={700} color="primary.main">
                {gridSummary.totalSessions}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: 'form.behaviorInterval.totalSessions' })}
              </Typography>
            </Stack>
          </MainCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MainCard>
            <Stack spacing={0.5}>
              <Typography variant="h3" fontWeight={700} color="info.main">
                {gridSummary.totalIntervals}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: 'form.label.totalIntervals' })}
              </Typography>
            </Stack>
          </MainCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MainCard>
            <Stack spacing={0.5}>
              <Typography variant="h3" fontWeight={700} color="success.main">
                {gridSummary.avgWhole}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: 'form.behaviorInterval.avgWholeInterval' })}
              </Typography>
            </Stack>
          </MainCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MainCard>
            <Stack spacing={0.5}>
              <Typography variant="h3" fontWeight={700} color="secondary.main">
                {gridSummary.avgPartial}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: 'form.behaviorInterval.avgPartialInterval' })}
              </Typography>
            </Stack>
          </MainCard>
        </Grid>
      </Grid>

      {/* Tabs for Data Entry and Historical Data */}
      <MainCard>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          rowGap={1}
          sx={{ borderBottom: 1, borderColor: 'divider', pb: { xs: 1, sm: 0 } }}
        >
          <Tabs value={gridTabValue} onChange={handleGridTabChange} variant="scrollable" scrollButtons={false} sx={{ minWidth: 0 }}>
            <Tab label={intl.formatMessage({ id: 'form.tab.dataEntry' })} />
            <Tab label={intl.formatMessage({ id: 'form.tab.historicalData' })} />
          </Tabs>
          <Stack direction="row" spacing={1} sx={{ pr: 2 }}>
            {gridTabValue === 0 && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PlusOutlined />}
                onClick={handleNewSession}
                disabled={inlineEditing}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {intl.formatMessage({ id: 'form.behaviorInterval.newSession' })}
              </Button>
            )}
            <CSVExport
              data={csvData}
              headers={[
                { label: 'Date', key: 'date' },
                { label: 'Behavior', key: 'behavior' },
                { label: 'Interval Type', key: 'intervalType' },
                { label: 'Interval Length (s)', key: 'intervalLength' },
                { label: 'Total Intervals', key: 'totalIntervals' },
                { label: 'Whole Count', key: 'wholeCount' },
                { label: 'Whole %', key: 'wholePercent' },
                { label: 'Partial Count', key: 'partialCount' },
                { label: 'Partial %', key: 'partialPercent' },
                { label: 'None Count', key: 'noneCount' },
                { label: 'None %', key: 'nonePercent' },
                { label: 'Missed Count', key: 'missedCount' },
                { label: 'Missed %', key: 'missedPercent' },
                { label: 'Notes', key: 'notes' }
              ]}
              filename="behavior-interval-recording.csv"
            />
          </Stack>
        </Stack>

        <Box sx={{ pt: 3 }}>
          {/* Tab 0: Data Entry - Today's Sessions */}
          {gridTabValue === 0 && (
            <>
              {todaySessions.length === 0 && !inlineEditing ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    {intl.formatMessage({ id: 'form.behaviorInterval.noSessionsToday' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Click &quot;+ New Session&quot; to start recording
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width={50} />
                        <TableCell>{intl.formatMessage({ id: 'form.behaviorInterval.dateTime' })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'dataCollection.field.behavior' })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'behaviorInterval.type' })}</TableCell>
                        <TableCell align="center">{intl.formatMessage({ id: 'behaviorInterval.total' })}</TableCell>
                        <TableCell align="center">{intl.formatMessage({ id: 'dataCollection.field.duration' })}</TableCell>
                        <TableCell align="center">{intl.formatMessage({ id: 'behaviorInterval.interval' })}</TableCell>
                        <TableCell align="center">{intl.formatMessage({ id: 'dataCollection.intervalLabel.whole' })}</TableCell>
                        <TableCell align="center">{intl.formatMessage({ id: 'dataCollection.intervalLabel.partial' })}</TableCell>
                        <TableCell align="center">{intl.formatMessage({ id: 'dataCollection.intervalLabel.none' })}</TableCell>
                        <TableCell align="center">{intl.formatMessage({ id: 'dataCollection.intervalLabel.missed' })}</TableCell>
                        <TableCell align="center">{intl.formatMessage({ id: 'behaviorInterval.actions' })}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inlineEditing && renderInlineEditRow()}
                      {todaySessions.map((session) => renderSessionRow(session, expandedRow === session.id))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Tab 1: Historical Data - paginated 5 at a time */}
          {gridTabValue === 1 && (
            <>
              {historicalSessions.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    {intl.formatMessage({ id: 'form.behaviorInterval.noHistoricalData' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Sessions will appear here once recorded
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell width={50} />
                          <TableCell>{intl.formatMessage({ id: 'form.behaviorInterval.dateTime' })}</TableCell>
                          <TableCell>{intl.formatMessage({ id: 'dataCollection.field.behavior' })}</TableCell>
                          <TableCell>{intl.formatMessage({ id: 'behaviorInterval.type' })}</TableCell>
                          <TableCell align="center">{intl.formatMessage({ id: 'behaviorInterval.total' })}</TableCell>
                          <TableCell align="center">{intl.formatMessage({ id: 'dataCollection.field.duration' })}</TableCell>
                          <TableCell align="center">{intl.formatMessage({ id: 'behaviorInterval.interval' })}</TableCell>
                          <TableCell align="center">{intl.formatMessage({ id: 'dataCollection.intervalLabel.whole' })}</TableCell>
                          <TableCell align="center">{intl.formatMessage({ id: 'dataCollection.intervalLabel.partial' })}</TableCell>
                          <TableCell align="center">{intl.formatMessage({ id: 'dataCollection.intervalLabel.none' })}</TableCell>
                          <TableCell align="center">{intl.formatMessage({ id: 'dataCollection.intervalLabel.missed' })}</TableCell>
                          <TableCell align="center">{intl.formatMessage({ id: 'behaviorInterval.actions' })}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleHistoricalSessions.map((session) => renderSessionRow(session, expandedRow === session.id))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2, px: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage(
                        {
                          id: 'form.behaviorInterval.historicalShowing',
                          defaultMessage: 'Showing {shown} of {total}'
                        },
                        { shown: visibleHistoricalSessions.length, total: historicalSessions.length }
                      )}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {historicalVisibleCount > HISTORICAL_PAGE_SIZE && (
                        <Button size="small" variant="text" onClick={() => setHistoricalVisibleCount(HISTORICAL_PAGE_SIZE)}>
                          {intl.formatMessage({ id: 'form.behaviorInterval.showLess', defaultMessage: 'Show less' })}
                        </Button>
                      )}
                      {visibleHistoricalSessions.length < historicalSessions.length && (
                        <Button size="small" variant="outlined" onClick={() => setHistoricalVisibleCount((n) => n + HISTORICAL_PAGE_SIZE)}>
                          {intl.formatMessage({ id: 'form.behaviorInterval.loadMore', defaultMessage: 'Load more' })}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </>
              )}
            </>
          )}
        </Box>
      </MainCard>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{intl.formatMessage({ id: 'behaviorInterval.deleteSession' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this session? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

BehaviorIntervalMode.propTypes = {
  onDataChange: PropTypes.func,
  viewMode: PropTypes.oneOf(['individual', 'grid']),
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  childName: PropTypes.string,
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistName: PropTypes.string,
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  behaviors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  actionsRef: PropTypes.shape({ current: PropTypes.any })
};

export default BehaviorIntervalMode;
